// Read-only client-side progress helpers (round 2). These functions never
// decide whether a badge is earned — that's the server's job, surfaced via
// `users.badges`. They compute the "current value" for tiered/themed badges
// so the UI can render progress hints.

import { nftsData } from '@web/features/collection/data';
import { IPersonaFront } from '@web/utils/api/procedures';

import {
  ALL_BADGE_CODES,
  BADGES,
  BadgeCode,
  Trait,
} from './catalog';

const TRAITS_BY_TOKEN = new Map<number, Record<string, string>>();
for (const row of nftsData) {
  TRAITS_BY_TOKEN.set(row.id, row.traits as unknown as Record<string, string>);
}

interface PersonaWithTraits {
  tokenId: bigint;
  rank: number | null;
  blockchain: string;
  traits: Record<string, string>;
}

function enrich(personas: IPersonaFront[]): PersonaWithTraits[] {
  return personas.map((p) => ({
    tokenId: p.tokenId,
    rank: p.rank ?? null,
    blockchain: p.blockchain,
    traits: TRAITS_BY_TOKEN.get(Number(p.tokenId)) ?? {},
  }));
}

function distinctValuesFor(
  personas: PersonaWithTraits[],
  trait: Trait,
): number {
  const s = new Set<string>();
  for (const p of personas) {
    const v = p.traits[trait];
    if (v != null) s.add(v);
  }
  return s.size;
}

function themeCategoriesCovered(
  personas: PersonaWithTraits[],
  keyword: string,
  traits: Trait[],
): number {
  let covered = 0;
  for (const trait of traits) {
    const hit = personas.some((p) => (p.traits[trait] ?? '').includes(keyword));
    if (hit) covered += 1;
  }
  return covered;
}

function bestRank(personas: PersonaWithTraits[]): number | null {
  let best: number | null = null;
  for (const p of personas) {
    if (p.rank == null) continue;
    if (best == null || p.rank < best) best = p.rank;
  }
  return best;
}

// Returns the integer used for the progress display per badge. For tiered
// rarity badges we display "best rank in your collection" (lower is better),
// so the comparison sense flips.
export function measureBadgeValue(
  code: BadgeCode,
  personas: PersonaWithTraits[],
): number | undefined {
  const def = BADGES[code];
  if (def.tierGroupId === 'HOLDER') return personas.length;
  if (def.tierGroupId === 'RARITY') return bestRank(personas) ?? undefined;
  if (def.themeKeyword && def.themeTraits) {
    return themeCategoriesCovered(personas, def.themeKeyword, def.themeTraits);
  }
  if (def.trait && def.threshold != null) {
    return distinctValuesFor(personas, def.trait);
  }
  return undefined;
}

export interface BadgeState {
  code: BadgeCode;
  earned: boolean;
  current?: number;
  target?: number;
  // For RARITY: lower current is better. Tells the UI to flip the
  // comparison label.
  invertProgress?: boolean;
  isNextInGroup?: boolean;
}

const TIER_GROUPS: Record<string, BadgeCode[]> = (() => {
  const acc: Record<string, BadgeCode[]> = {};
  for (const code of ALL_BADGE_CODES) {
    const def = BADGES[code];
    if (!def.tierGroupId) continue;
    (acc[def.tierGroupId] ??= []).push(code);
  }
  for (const group of Object.values(acc)) {
    group.sort(
      (a, b) => (BADGES[a].threshold ?? 0) - (BADGES[b].threshold ?? 0),
    );
  }
  return acc;
})();

export function computeStates(
  earnedCodes: Set<string>,
  personas: IPersonaFront[],
): Record<BadgeCode, BadgeState> {
  const enriched = enrich(personas);
  const out = {} as Record<BadgeCode, BadgeState>;

  for (const code of ALL_BADGE_CODES) {
    const def = BADGES[code];
    const earned = earnedCodes.has(code);
    const current = measureBadgeValue(code, enriched);
    const target = def.threshold;
    const invertProgress = def.tierGroupId === 'RARITY';
    out[code] = { code, earned, current, target, invertProgress };
  }

  // Mark the next-tier goal within each group.
  for (const groupCodes of Object.values(TIER_GROUPS)) {
    const nextUnearned = groupCodes.find((c) => !out[c].earned);
    if (nextUnearned) out[nextUnearned].isNextInGroup = true;
  }

  return out;
}

export function findNextEarnableBadge(
  earnedCodes: Set<string>,
  personas: IPersonaFront[],
): BadgeCode | null {
  const states = computeStates(earnedCodes, personas);
  const nextInGroup = ALL_BADGE_CODES.find((c) => states[c].isNextInGroup);
  if (nextInGroup) return nextInGroup;
  const anyUnearned = ALL_BADGE_CODES.find((c) => !states[c].earned);
  return anyUnearned ?? null;
}

export function countEarned(
  earnedCodes: Set<string>,
): { earned: number; total: number } {
  let earned = 0;
  for (const code of ALL_BADGE_CODES) if (earnedCodes.has(code)) earned += 1;
  return { earned, total: ALL_BADGE_CODES.length };
}

// Filters a raw `users.badges` array down to codes that exist in the
// current catalog, preserving catalog order. Used by profile / holder
// list views that just want a list of icons.
export function getEarnedBadges(
  badges: readonly string[] | undefined,
): BadgeCode[] {
  if (!badges || badges.length === 0) return [];
  const set = new Set(badges);
  return ALL_BADGE_CODES.filter((c) => set.has(c));
}
